/** @jsxImportSource theme-ui */
import React, { MouseEvent, PropsWithChildren } from 'react';
import { SxProp } from 'theme-ui';

import { focusStyles, sxtheme, valignHackBottom } from 'utils/cssHelpers';

import { BdIcon } from 'components/_common/BdIcon';
import { InsightsIconLiteral } from 'components/_common/BdIcon/BdIcon';
import { TypographyColor } from 'components/_common/Typography/Typography';
import { BdTooltip } from 'components/_controls/BdTooltip';

type IconButtonProps = SxProp & {
  /** The icon that appears inside the button */
  icon: InsightsIconLiteral;
  /** The aria-label for screen readers */
  ariaLabel: string;
  /** The size of the button. 16px x 16px for small, 24px x 24px for medium, 40px x 40px for lage */
  size: 'small' | 'medium' | 'large';
  /** The colour of the the icon inside the component. If none are used it will be sea90 for default and white for invert  */
  color?: TypographyColor;
  bold?: boolean;
  disabled?: boolean;
  /** Will render text on the button on hover or focus. If no text is supplied no tooltip will render */
  tooltipText?: string;
  /** Invert the button focus and other colours for dark background */
  invert?: boolean;
  /** Custom CSS class name */
  className?: string;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
};

export const IconButton = React.forwardRef<HTMLButtonElement, PropsWithChildren<IconButtonProps>>(
  (
    {
      icon,
      ariaLabel,
      size,
      color = null,
      bold = false,
      disabled = false,
      tooltipText,
      invert = false,
      className,
      onClick,
      ...props
    },
    ref
  ) => {
    const iconButtonSize = size === 'small' ? '16px' : size === 'medium' ? '24px' : '40px';
    const iconSize = size === 'small' ? '8px' : '16px';

    const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      !disabled && onClick && onClick(e);
    };

    const button = (
      <button
        data-testid={`icon-button-${icon}`}
        aria-label={ariaLabel}
        onClick={handleClick}
        type="button"
        disabled={disabled}
        className={className}
        sx={{
          display: 'inline-flex',
          background: 'transparent',
          border: 'none',
          borderRadius: '2px',
          height: iconButtonSize,
          width: iconButtonSize,
          p: size === 'large' ? '12px' : '4px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          '& + &': {
            ml: sxtheme(t => t.misc.panelIconXSpacing),
          },
          '&:hover': {
            backgroundColor: invert ? 'rgba(255, 255, 255, 0.1)' : 'rgba(78, 97, 120, 0.2)',
          },
          '&:focus': focusStyles({ tight: true, invert: invert }),
          '&:active': {
            backgroundColor: sxtheme(t =>
              invert ? 'rgba(255, 255, 255, 0.2)' : t.colors.seafoam20
            ),
          },
          ...props.sx,
        }}
        {...props}
      >
        <BdIcon
          name={icon}
          color={color ?? (invert ? 'white' : 'sea90')}
          bold={bold}
          nomargin
          sx={{
            ...valignHackBottom('-1px'),
            '&.icon.bd-icon': {
              fontSize: iconSize,
              width: iconSize,
            },
          }}
        />
      </button>
    );

    return tooltipText?.length ? (
      <BdTooltip text={tooltipText} wide>
        {button}
      </BdTooltip>
    ) : (
      button
    );
  }
);
